import React, { type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'

import { Dashboard, PageLayout, TabPills } from '@components/common'
import type { TabPillsProps } from '@components/common/TabPills/types'
import { useAppearance } from '@fourthwall/appearance'
import {
  Dropdown,
  type DropdownItemProps,
  HeadContent,
  ToolbarButton,
} from '@fourthwall/components'
import { DotsHorizontalIcon } from '@fourthwall/icons'
import {
  AnalyticsBarChart,
  AnalyticsCard,
  AnalyticsChart,
  AnalyticsLayout,
  AnalyticsRows,
} from '@fourthwall/module-analytics'
import { isWebView } from '@fourthwall/utils'
import type { LocationState } from '@types'

import type { ExternalSource } from '../../Analytics.types'
import { AnalyticsToolbar, PrecisionSelect, RangeSelect } from '../../modules/AnalyticsParams'
import * as S from './AnalyticsOverview.styled'
import type {
  ContributionValueChartProps,
  ContributionsChartProps,
  ConversionRatesChartProps,
  MembershipsFreeMembersChangeChartProps,
  MembershipsMembersChangeChartProps,
  MembershipsProfitChartProps,
  MembershipsTopTiersChartProps,
  MembershipsTotalFreeMembersChartProps,
  MembershipsTotalMembersChartProps,
  MembershipsTransactionsChartProps,
  PaymentMethodUsedChartProps,
  ProfitChartProps,
  SalesByCountryChartProps,
  SalesBySocialSourceTableChartProps,
  SalesByTrafficSourceTableChartProps,
  SessionsBySocialSourceChartProps,
  SessionsByTrafficSourceChartProps,
  ShopOrdersChartProps,
  ShopProfitChartProps,
  TopProductsChartProps,
  TotalProfitChartProps,
  UniqueVisitorsChartProps,
} from './AnalyticsOverview.types'
import { externalSourceIcons } from './consts'
import { useAnalyticsCharts } from './hooks/useAnalyticsCharts'

export type AnalyticsViewProps = {
  title: string
  toolbarItems: DropdownItemProps[]
  barContent?: ReactNode
  tabs: TabPillsProps['options']
  activeTab: string
  onTabChange: (value: string) => void
  profitChart?: ProfitChartProps
  totalProfitChart?: TotalProfitChartProps
  shopProfitChart?: ShopProfitChartProps
  membershipsProfitChart?: MembershipsProfitChartProps
  membershipsTotalMembersChart?: MembershipsTotalMembersChartProps
  membershipsTotalFreeMembersChart?: MembershipsTotalFreeMembersChartProps
  membershipsMembersChangeChart?: MembershipsMembersChangeChartProps
  membershipsFreeMembersChangeChart?: MembershipsFreeMembersChangeChartProps
  contributionsChart?: ContributionsChartProps
  shopOrdersChart?: ShopOrdersChartProps
  membershipsTransactionsChart?: MembershipsTransactionsChartProps
  contributionValueChart?: ContributionValueChartProps
  topProductsChart?: TopProductsChartProps
  salesByCountryChart?: SalesByCountryChartProps
  membershipsTopTiersChart?: MembershipsTopTiersChartProps
  paymentMethodUsedChart?: PaymentMethodUsedChartProps
  conversionRatesChart?: ConversionRatesChartProps
  uniqueVisitorsChart?: UniqueVisitorsChartProps
  salesByTrafficSourceTableChart?: SalesByTrafficSourceTableChartProps
  sessionsByTrafficSourceChart?: SessionsByTrafficSourceChartProps
  salesBySocialSourceTableChart?: SalesBySocialSourceTableChartProps
  sessionsBySocialSourceChart?: SessionsBySocialSourceChartProps
}

export const AnalyticsOverviewView = ({
  title,
  toolbarItems,
  barContent,
  tabs,
  activeTab,
  onTabChange,
  profitChart,
  totalProfitChart,
  shopProfitChart,
  membershipsProfitChart,
  membershipsTotalMembersChart,
  membershipsTotalFreeMembersChart,
  membershipsMembersChangeChart,
  membershipsFreeMembersChangeChart,
  contributionsChart,
  shopOrdersChart,
  membershipsTransactionsChart,
  contributionValueChart,
  topProductsChart,
  salesByCountryChart,
  membershipsTopTiersChart,
  paymentMethodUsedChart,
  conversionRatesChart,
  uniqueVisitorsChart,
  salesByTrafficSourceTableChart,
  sessionsByTrafficSourceChart,
  salesBySocialSourceTableChart,
  sessionsBySocialSourceChart,
}: AnalyticsViewProps) => {
  const { appearance } = useAppearance()
  const location = useLocation()
  const visibleCharts = useAnalyticsCharts(
    activeTab,
    {
      profitChart,
      totalProfitChart,
      shopProfitChart,
      membershipsProfitChart,
      membershipsTotalMembersChart,
      membershipsTotalFreeMembersChart,
      membershipsMembersChangeChart,
      membershipsFreeMembersChangeChart,
      contributionsChart,
      shopOrdersChart,
      membershipsTransactionsChart,
      contributionValueChart,
      topProductsChart,
      salesByCountryChart,
      membershipsTopTiersChart,
      paymentMethodUsedChart,
      conversionRatesChart,
      uniqueVisitorsChart,
      salesByTrafficSourceTableChart,
      sessionsByTrafficSourceChart,
      salesBySocialSourceTableChart,
      sessionsBySocialSourceChart,
    },
    tabs.length > 0,
  )

  const locationState = {
    from: { label: title, location },
  } satisfies LocationState

  const getExternalSourceIcon = (externalSource?: ExternalSource) =>
    externalSource ? externalSourceIcons[externalSource] : undefined

  return (
    <>
      <HeadContent title={title} titleTemplate={isWebView() ? title : undefined} />
      <PageLayout size="full">
        <Dashboard.Section
          name={isWebView() ? undefined : title}
          toolbarContent={
            !isWebView() && toolbarItems.length ? (
              <Dropdown
                content={toolbarItems.map((toolbarDropdownItem, index) => (
                  <Dropdown.Item key={index} {...toolbarDropdownItem} />
                ))}
                position="bottom-end"
              >
                <ToolbarButton label="Settings" icon={DotsHorizontalIcon} />
              </Dropdown>
            ) : undefined
          }
        >
          {tabs.length > 0 && (
            <S.TabPillsContainer>
              <TabPills size="large" options={tabs} value={activeTab} onChange={onTabChange} />
            </S.TabPillsContainer>
          )}
          <AnalyticsToolbar>
            <RangeSelect excludedPrecision={['WEEK', 'MONTH', 'YEAR']} />
            <PrecisionSelect excludedPrecision={['WEEK', 'MONTH', 'YEAR']} />
          </AnalyticsToolbar>
          {barContent ? <div style={{ marginBlock: 20 }}>{barContent}</div> : null}
          <AnalyticsLayout>
            {visibleCharts.map(({ name, chart }) => {
              if (!chart) return null

              switch (name) {
                case 'profitChart': {
                  const profitChart = chart as ProfitChartProps

                  return (
                    <AnalyticsLayout.Item key={name} featured>
                      <AnalyticsCard
                        title={profitChart.title}
                        reportTo={profitChart.reportTo}
                        state={locationState}
                        value={profitChart.value}
                        statistics={
                          profitChart.series ? (
                            <AnalyticsChart
                              series={profitChart.series}
                              description={profitChart.range}
                              formatDate={profitChart.formatDate}
                              formatValue={profitChart.formatValue}
                              appearance={appearance}
                            />
                          ) : undefined
                        }
                        isLoading={profitChart.isLoading}
                        isError={profitChart.isError}
                      />
                    </AnalyticsLayout.Item>
                  )
                }
                case 'totalProfitChart': {
                  const totalProfitChart = chart as TotalProfitChartProps

                  return (
                    <AnalyticsLayout.Item key={name} featured>
                      <AnalyticsCard
                        title={totalProfitChart.title}
                        reportTo={totalProfitChart.reportTo}
                        state={locationState}
                        value={totalProfitChart.value}
                        statistics={
                          totalProfitChart.series ? (
                            <AnalyticsChart
                              series={totalProfitChart.series}
                              description={totalProfitChart.range}
                              formatDate={totalProfitChart.formatDate}
                              formatValue={totalProfitChart.formatValue}
                              appearance={appearance}
                            />
                          ) : undefined
                        }
                        isLoading={totalProfitChart.isLoading}
                        isError={totalProfitChart.isError}
                      />
                    </AnalyticsLayout.Item>
                  )
                }
                case 'shopProfitChart': {
                  const shopProfitChart = chart as ProfitChartProps

                  return (
                    <AnalyticsLayout.Item key={name} featured>
                      <AnalyticsCard
                        title={shopProfitChart.title}
                        reportTo={shopProfitChart.reportTo}
                        state={locationState}
                        value={shopProfitChart.value}
                        statistics={
                          shopProfitChart.series ? (
                            <AnalyticsChart
                              series={shopProfitChart.series}
                              description={shopProfitChart.range}
                              formatDate={shopProfitChart.formatDate}
                              formatValue={shopProfitChart.formatValue}
                              appearance={appearance}
                            />
                          ) : undefined
                        }
                        isLoading={shopProfitChart.isLoading}
                        isError={shopProfitChart.isError}
                      />
                    </AnalyticsLayout.Item>
                  )
                }
                case 'membershipsProfitChart': {
                  const membershipsProfitChart = chart as ProfitChartProps

                  return (
                    <AnalyticsLayout.Item key={name} featured>
                      <AnalyticsCard
                        title={membershipsProfitChart.title}
                        reportTo={membershipsProfitChart.reportTo}
                        state={locationState}
                        value={membershipsProfitChart.value}
                        statistics={
                          membershipsProfitChart.series ? (
                            <AnalyticsChart
                              series={membershipsProfitChart.series}
                              description={membershipsProfitChart.range}
                              formatDate={membershipsProfitChart.formatDate}
                              formatValue={membershipsProfitChart.formatValue}
                              appearance={appearance}
                            />
                          ) : undefined
                        }
                        isLoading={membershipsProfitChart.isLoading}
                        isError={membershipsProfitChart.isError}
                      />
                    </AnalyticsLayout.Item>
                  )
                }
                case 'membershipsTotalMembersChart': {
                  const membershipsTotalMembersChart = chart as MembershipsTotalMembersChartProps

                  return (
                    <AnalyticsLayout.Item key={name}>
                      <AnalyticsCard
                        title={membershipsTotalMembersChart.title}
                        reportTo={membershipsTotalMembersChart.reportTo}
                        state={locationState}
                        value={membershipsTotalMembersChart.value}
                        statistics={
                          membershipsTotalMembersChart.series ? (
                            <AnalyticsChart
                              description={membershipsTotalMembersChart.range}
                              series={membershipsTotalMembersChart.series}
                              formatDate={membershipsTotalMembersChart.formatDate}
                              formatValue={membershipsTotalMembersChart.formatValue}
                              appearance={appearance}
                            />
                          ) : undefined
                        }
                        isLoading={membershipsTotalMembersChart.isLoading}
                        isError={membershipsTotalMembersChart.isError}
                      />
                    </AnalyticsLayout.Item>
                  )
                }
                case 'membershipsMembersChangeChart': {
                  const membershipsMembersChangeChart = chart as MembershipsMembersChangeChartProps

                  return (
                    <AnalyticsLayout.Item key={name}>
                      <AnalyticsCard
                        title={membershipsMembersChangeChart.title}
                        reportTo={membershipsMembersChangeChart.reportTo}
                        state={locationState}
                        value={membershipsMembersChangeChart.value}
                        statistics={
                          membershipsMembersChangeChart.series ? (
                            <AnalyticsChart
                              description={membershipsMembersChangeChart.range}
                              series={membershipsMembersChangeChart.series}
                              formatDate={membershipsMembersChangeChart.formatDate}
                              formatValue={membershipsMembersChangeChart.formatValue}
                              appearance={appearance}
                            />
                          ) : undefined
                        }
                        isLoading={membershipsMembersChangeChart.isLoading}
                        isError={membershipsMembersChangeChart.isError}
                      />
                    </AnalyticsLayout.Item>
                  )
                }
                case 'membershipsTotalFreeMembersChart': {
                  const membershipsTotalFreeMembersChart =
                    chart as MembershipsTotalFreeMembersChartProps

                  return (
                    <AnalyticsLayout.Item key={name}>
                      <AnalyticsCard
                        title={membershipsTotalFreeMembersChart.title}
                        tooltip={membershipsTotalFreeMembersChart.tooltip}
                        reportTo={membershipsTotalFreeMembersChart.reportTo}
                        state={locationState}
                        value={membershipsTotalFreeMembersChart.value}
                        statistics={
                          membershipsTotalFreeMembersChart.series ? (
                            <AnalyticsChart
                              description={membershipsTotalFreeMembersChart.range}
                              series={membershipsTotalFreeMembersChart.series}
                              formatDate={membershipsTotalFreeMembersChart.formatDate}
                              formatValue={membershipsTotalFreeMembersChart.formatValue}
                              appearance={appearance}
                            />
                          ) : undefined
                        }
                        isLoading={membershipsTotalFreeMembersChart.isLoading}
                        isError={membershipsTotalFreeMembersChart.isError}
                      />
                    </AnalyticsLayout.Item>
                  )
                }
                case 'membershipsFreeMembersChangeChart': {
                  const membershipsFreeMembersChangeChart =
                    chart as MembershipsFreeMembersChangeChartProps

                  return (
                    <AnalyticsLayout.Item key={name}>
                      <AnalyticsCard
                        title={membershipsFreeMembersChangeChart.title}
                        reportTo={membershipsFreeMembersChangeChart.reportTo}
                        state={locationState}
                        value={membershipsFreeMembersChangeChart.value}
                        statistics={
                          membershipsFreeMembersChangeChart.series ? (
                            <AnalyticsChart
                              description={membershipsFreeMembersChangeChart.range}
                              series={membershipsFreeMembersChangeChart.series}
                              formatDate={membershipsFreeMembersChangeChart.formatDate}
                              formatValue={membershipsFreeMembersChangeChart.formatValue}
                              appearance={appearance}
                            />
                          ) : undefined
                        }
                        isLoading={membershipsFreeMembersChangeChart.isLoading}
                        isError={membershipsFreeMembersChangeChart.isError}
                      />
                    </AnalyticsLayout.Item>
                  )
                }
                case 'contributionsChart': {
                  const contributionsChart = chart as ContributionsChartProps

                  return (
                    <AnalyticsLayout.Item key={name}>
                      <AnalyticsCard
                        title={contributionsChart.title}
                        reportTo={contributionsChart.reportTo}
                        state={locationState}
                        value={contributionsChart.value}
                        statistics={
                          contributionsChart.series ? (
                            <AnalyticsChart
                              description={contributionsChart.range}
                              series={contributionsChart.series}
                              formatDate={contributionsChart.formatDate}
                              formatValue={contributionsChart.formatValue}
                              appearance={appearance}
                            />
                          ) : undefined
                        }
                        isLoading={contributionsChart.isLoading}
                        isError={contributionsChart.isError}
                      />
                    </AnalyticsLayout.Item>
                  )
                }
                case 'shopOrdersChart': {
                  const shopOrdersChart = chart as ShopOrdersChartProps

                  return (
                    <AnalyticsLayout.Item key={name}>
                      <AnalyticsCard
                        title={shopOrdersChart.title}
                        reportTo={shopOrdersChart.reportTo}
                        state={locationState}
                        value={shopOrdersChart.value}
                        statistics={
                          shopOrdersChart.series ? (
                            <AnalyticsChart
                              description={shopOrdersChart.range}
                              series={shopOrdersChart.series}
                              formatDate={shopOrdersChart.formatDate}
                              formatValue={shopOrdersChart.formatValue}
                              appearance={appearance}
                            />
                          ) : undefined
                        }
                        isLoading={shopOrdersChart.isLoading}
                        isError={shopOrdersChart.isError}
                      />
                    </AnalyticsLayout.Item>
                  )
                }
                case 'membershipsTransactionsChart': {
                  const membershipsTransactionsChart = chart as MembershipsTransactionsChartProps

                  return (
                    <AnalyticsLayout.Item key={name}>
                      <AnalyticsCard
                        title={membershipsTransactionsChart.title}
                        reportTo={membershipsTransactionsChart.reportTo}
                        state={locationState}
                        value={membershipsTransactionsChart.value}
                        statistics={
                          membershipsTransactionsChart.series ? (
                            <AnalyticsChart
                              description={membershipsTransactionsChart.range}
                              series={membershipsTransactionsChart.series}
                              formatDate={membershipsTransactionsChart.formatDate}
                              formatValue={membershipsTransactionsChart.formatValue}
                              appearance={appearance}
                            />
                          ) : undefined
                        }
                        isLoading={membershipsTransactionsChart.isLoading}
                        isError={membershipsTransactionsChart.isError}
                      />
                    </AnalyticsLayout.Item>
                  )
                }
                case 'contributionValueChart': {
                  const contributionValueChart = chart as ContributionValueChartProps

                  return (
                    <AnalyticsLayout.Item key={name}>
                      <AnalyticsCard
                        title={contributionValueChart.title}
                        reportTo={contributionValueChart.reportTo}
                        state={locationState}
                        value={contributionValueChart.value}
                        statistics={
                          contributionValueChart.series ? (
                            <AnalyticsChart
                              description={contributionValueChart.range}
                              series={contributionValueChart.series}
                              formatDate={contributionValueChart.formatDate}
                              formatValue={contributionValueChart.formatValue}
                              appearance={appearance}
                            />
                          ) : undefined
                        }
                        isLoading={contributionValueChart.isLoading}
                        isError={contributionValueChart.isError}
                      />
                    </AnalyticsLayout.Item>
                  )
                }
                case 'topProductsChart': {
                  const topProductsChart = chart as TopProductsChartProps

                  return (
                    <AnalyticsLayout.Item key={name}>
                      <AnalyticsCard
                        title={topProductsChart.title}
                        reportTo={topProductsChart.reportTo}
                        state={locationState}
                        description={topProductsChart.range}
                        statistics={
                          <AnalyticsRows
                            rows={topProductsChart.rows}
                            emptyMessage="No contributions found in this date range."
                          />
                        }
                        isLoading={topProductsChart.isLoading}
                        isError={topProductsChart.isError}
                      />
                    </AnalyticsLayout.Item>
                  )
                }
                case 'salesByCountryChart': {
                  const salesByCountryChart = chart as SalesByCountryChartProps

                  return (
                    <AnalyticsLayout.Item key={name}>
                      <AnalyticsCard
                        title={salesByCountryChart.title}
                        reportTo={salesByCountryChart.reportTo}
                        state={locationState}
                        description={salesByCountryChart.range}
                        statistics={
                          <AnalyticsRows
                            rows={salesByCountryChart.rows}
                            emptyMessage="No contributions found in this date range."
                          />
                        }
                        isLoading={salesByCountryChart.isLoading}
                        isError={salesByCountryChart.isError}
                      />
                    </AnalyticsLayout.Item>
                  )
                }
                case 'membershipsTopTiersChart': {
                  const membershipsTopTiersChart = chart as MembershipsTopTiersChartProps

                  return (
                    <AnalyticsLayout.Item key={name}>
                      <AnalyticsCard
                        title={membershipsTopTiersChart.title}
                        reportTo={membershipsTopTiersChart.reportTo}
                        state={locationState}
                        description={membershipsTopTiersChart.range}
                        statistics={
                          <AnalyticsRows
                            rows={membershipsTopTiersChart.rows}
                            emptyMessage="No data found in this date range."
                          />
                        }
                        isLoading={membershipsTopTiersChart.isLoading}
                        isError={membershipsTopTiersChart.isError}
                      />
                    </AnalyticsLayout.Item>
                  )
                }
                case 'paymentMethodUsedChart': {
                  const paymentMethodUsedChart = chart as PaymentMethodUsedChartProps

                  return (
                    <AnalyticsLayout.Item key={name}>
                      <AnalyticsCard
                        title={paymentMethodUsedChart.title}
                        description={paymentMethodUsedChart.range}
                        reportTo={paymentMethodUsedChart.reportTo}
                        state={locationState}
                        statistics={
                          <AnalyticsBarChart
                            {...paymentMethodUsedChart.barChart}
                            emptyMessage="No data found in this date range."
                          />
                        }
                        isLoading={paymentMethodUsedChart.isLoading}
                        isError={paymentMethodUsedChart.isError}
                      />
                    </AnalyticsLayout.Item>
                  )
                }
                case 'conversionRatesChart': {
                  const conversionRatesChart = chart as ConversionRatesChartProps

                  return (
                    <AnalyticsLayout.Item key={name}>
                      <AnalyticsCard
                        title={conversionRatesChart.title}
                        icon={getExternalSourceIcon(conversionRatesChart.externalSource)}
                        value={conversionRatesChart.value}
                        description={conversionRatesChart.range}
                        reportTo={conversionRatesChart.reportTo}
                        state={locationState}
                        statistics={
                          <AnalyticsRows
                            rows={conversionRatesChart.rows}
                            emptyMessage="No data found in this date range."
                          />
                        }
                        isLoading={conversionRatesChart.isLoading}
                        isError={conversionRatesChart.isError}
                      />
                    </AnalyticsLayout.Item>
                  )
                }
                case 'uniqueVisitorsChart': {
                  const uniqueVisitorsChart = chart as UniqueVisitorsChartProps

                  return (
                    <AnalyticsLayout.Item key={name}>
                      <AnalyticsCard
                        title={uniqueVisitorsChart.title}
                        icon={getExternalSourceIcon(uniqueVisitorsChart.externalSource)}
                        value={uniqueVisitorsChart.value}
                        reportTo={uniqueVisitorsChart.reportTo}
                        state={locationState}
                        statistics={
                          <AnalyticsChart
                            series={uniqueVisitorsChart.series}
                            description={uniqueVisitorsChart.range}
                            formatDate={uniqueVisitorsChart.formatDate}
                            formatValue={uniqueVisitorsChart.formatValue}
                            minSecondaryAxesValue={0}
                            appearance={appearance}
                          />
                        }
                        isLoading={uniqueVisitorsChart.isLoading}
                        isError={uniqueVisitorsChart.isError}
                      />
                    </AnalyticsLayout.Item>
                  )
                }
                case 'salesByTrafficSourceTableChart': {
                  const salesByTrafficSourceTableChart =
                    chart as SalesByTrafficSourceTableChartProps

                  return (
                    <AnalyticsLayout.Item key={name}>
                      <AnalyticsCard
                        title={salesByTrafficSourceTableChart.title}
                        icon={getExternalSourceIcon(salesByTrafficSourceTableChart.externalSource)}
                        description={salesByTrafficSourceTableChart.range}
                        reportTo={salesByTrafficSourceTableChart.reportTo}
                        state={locationState}
                        statistics={
                          <AnalyticsBarChart
                            {...salesByTrafficSourceTableChart.barChart}
                            emptyMessage="No data found in this date range."
                          />
                        }
                        isLoading={salesByTrafficSourceTableChart.isLoading}
                        isError={salesByTrafficSourceTableChart.isError}
                      />
                    </AnalyticsLayout.Item>
                  )
                }
                case 'sessionsByTrafficSourceChart': {
                  const sessionsByTrafficSourceChart = chart as SessionsByTrafficSourceChartProps

                  return (
                    <AnalyticsLayout.Item key={name}>
                      <AnalyticsCard
                        title={sessionsByTrafficSourceChart.title}
                        icon={getExternalSourceIcon(sessionsByTrafficSourceChart.externalSource)}
                        description={sessionsByTrafficSourceChart.range}
                        reportTo={sessionsByTrafficSourceChart.reportTo}
                        state={locationState}
                        statistics={
                          <AnalyticsBarChart
                            {...sessionsByTrafficSourceChart.barChart}
                            emptyMessage="No data found in this date range."
                          />
                        }
                        isLoading={sessionsByTrafficSourceChart.isLoading}
                        isError={sessionsByTrafficSourceChart.isError}
                      />
                    </AnalyticsLayout.Item>
                  )
                }
                case 'salesBySocialSourceTableChart': {
                  const salesBySocialSourceTableChart = chart as SalesBySocialSourceTableChartProps

                  return (
                    <AnalyticsLayout.Item key={name}>
                      <AnalyticsCard
                        title={salesBySocialSourceTableChart.title}
                        icon={getExternalSourceIcon(salesBySocialSourceTableChart.externalSource)}
                        description={salesBySocialSourceTableChart.range}
                        reportTo={salesBySocialSourceTableChart.reportTo}
                        state={locationState}
                        statistics={
                          <AnalyticsBarChart
                            {...salesBySocialSourceTableChart.barChart}
                            emptyMessage="No data found in this date range."
                          />
                        }
                        isLoading={salesBySocialSourceTableChart.isLoading}
                        isError={salesBySocialSourceTableChart.isError}
                      />
                    </AnalyticsLayout.Item>
                  )
                }
                case 'sessionsBySocialSourceChart': {
                  const sessionsBySocialSourceChart = chart as SessionsBySocialSourceChartProps

                  return (
                    <AnalyticsLayout.Item key={name}>
                      <AnalyticsCard
                        title={sessionsBySocialSourceChart.title}
                        icon={getExternalSourceIcon(sessionsBySocialSourceChart.externalSource)}
                        description={sessionsBySocialSourceChart.range}
                        reportTo={sessionsBySocialSourceChart.reportTo}
                        state={locationState}
                        statistics={
                          <AnalyticsBarChart
                            {...sessionsBySocialSourceChart.barChart}
                            emptyMessage="No data found in this date range."
                          />
                        }
                        isLoading={sessionsBySocialSourceChart.isLoading}
                        isError={sessionsBySocialSourceChart.isError}
                      />
                    </AnalyticsLayout.Item>
                  )
                }
                default:
                  return null
              }
            })}
          </AnalyticsLayout>
        </Dashboard.Section>
      </PageLayout>
    </>
  )
}
